import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken, logoutSession } from '../utils/authSession';
import { apiGet, apiPost } from '../api/client';

function statusBadge(status) {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running') return 'warning';
  return 'secondary';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function AdminBackups() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [runningBackup, setRunningBackup] = useState(false);
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${getAccessToken()}` }
  }), []);

  const loadData = useCallback(async () => {
    setError('');
    try {
      const [statusData, backupData] = await Promise.all([
        apiGet('/api/admin/backups/status', authHeaders()),
        apiGet('/api/admin/backups', authHeaders())
      ]);
      setStatus(statusData);
      setBackups(backupData.backups || []);
    } catch (err) {
      if (err.response?.status === 401) {
        await logoutSession({ redirect: false });
        navigate('/admin/login');
        return;
      }
      if (err.response?.status === 403) {
        navigate('/admin/dashboard');
        return;
      }
      setError(err.response?.data?.error || 'Failed to load backup information.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRunBackup = async () => {
    setMessage('');
    setError('');
    setRunningBackup(true);

    try {
      const result = await apiPost('/api/admin/backups/run', {}, authHeaders());
      setMessage(result.message || 'Backup completed successfully.');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Backup failed.');
      await loadData();
    } finally {
      setRunningBackup(false);
    }
  };

  const lastRunFailed = status?.lastRun?.status === 'failed';
  const backupInProgress = Boolean(status?.running || runningBackup);

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h1 className="h3 mb-1">System Backups</h1>
            <p className="text-muted mb-0">
              Database and uploads snapshots for disaster recovery.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRunBackup}
            disabled={backupInProgress}
          >
            {backupInProgress ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Running backup...
              </>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up me-2" />
                Run backup now
              </>
            )}
          </button>
        </div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {lastRunFailed && (
          <div className="alert alert-warning">
            <strong>Last automatic backup failed.</strong>{' '}
            {status.lastRun.error ? `Reason: ${status.lastRun.error}. ` : ''}
            Use <strong>Run backup now</strong> to create a fresh backup manually.
          </div>
        )}

        <div className="row g-3 mb-4">
          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h5 card-title">Where backups are saved</h2>
                <p className="mb-2">
                  Each backup is stored on the server in its own folder:
                </p>
                <code className="d-block text-break mb-3">{status?.backupDir || '—'}</code>
                <ul className="small text-muted mb-0">
                  <li><code>database.sql</code> — full database export</li>
                  <li><code>uploads/</code> — copy of uploaded images and videos</li>
                  <li><code>manifest.json</code> — backup metadata</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h5 card-title">Auto-backup status</h2>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Automatic backup</dt>
                  <dd className="col-sm-7">
                    <span className={`badge bg-${status?.autoBackupEnabled ? 'success' : 'secondary'}`}>
                      {status?.autoBackupEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </dd>

                  <dt className="col-sm-5">Schedule</dt>
                  <dd className="col-sm-7">
                    {status?.autoBackupEnabled
                      ? `Every ${status.backupIntervalDays} day(s)`
                      : 'Not scheduled in app'}
                  </dd>

                  <dt className="col-sm-5">Retention</dt>
                  <dd className="col-sm-7">Keep latest {status?.retentionCount || 10} backups</dd>

                  <dt className="col-sm-5">Last run</dt>
                  <dd className="col-sm-7">
                    {status?.lastRun ? (
                      <>
                        <span className={`badge bg-${statusBadge(status.lastRun.status)} me-2 text-capitalize`}>
                          {status.lastRun.status}
                        </span>
                        {formatDate(status.lastRun.finishedAt || status.lastRun.startedAt)}
                      </>
                    ) : 'No backup run recorded yet'}
                  </dd>

                  <dt className="col-sm-5">Latest backup</dt>
                  <dd className="col-sm-7">
                    {status?.latestBackup
                      ? `${status.latestBackup.folderName} (${status.latestBackup.totalSizeLabel})`
                      : 'None yet'}
                  </dd>
                </dl>
                {status?.nextAutoBackupHint && (
                  <p className="small text-muted mt-3 mb-0">{status.nextAutoBackupHint}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">Backup history</h2>
            <span className="badge bg-secondary">{backups.length} saved</span>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Backup folder</th>
                  <th>Created</th>
                  <th>Method</th>
                  <th>Database</th>
                  <th>Uploads</th>
                  <th>Total size</th>
                </tr>
              </thead>
              <tbody>
                {backups.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No backups found yet. Click <strong>Run backup now</strong> to create the first one.
                    </td>
                  </tr>
                ) : (
                  backups.map((backup) => (
                    <tr key={backup.id}>
                      <td>
                        <code>{backup.folderName}</code>
                        <div className="small text-muted text-break">{backup.path}</div>
                      </td>
                      <td>{formatDate(backup.createdAt)}</td>
                      <td className="text-capitalize">{backup.method}</td>
                      <td>{backup.hasDatabaseFile ? backup.databaseSizeLabel : 'Missing'}</td>
                      <td>{backup.hasUploadsFolder ? backup.uploadsSizeLabel : 'Not copied'}</td>
                      <td>{backup.totalSizeLabel}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {(status?.history || []).length > 0 && (
          <div className="card mt-4">
            <div className="card-header">
              <h2 className="h5 mb-0">Recent backup attempts</h2>
            </div>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Trigger</th>
                    <th>Started</th>
                    <th>Finished</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {status.history.map((entry, index) => (
                    <tr key={`${entry.startedAt}-${index}`}>
                      <td>
                        <span className={`badge bg-${statusBadge(entry.status)} text-capitalize`}>
                          {entry.status}
                        </span>
                      </td>
                      <td>{entry.trigger || '—'}</td>
                      <td>{formatDate(entry.startedAt)}</td>
                      <td>{formatDate(entry.finishedAt)}</td>
                      <td className="text-break">
                        {entry.error
                          ? entry.error
                          : entry.backupId
                            ? `${entry.backupId} (${entry.totalSizeLabel || entry.method || 'ok'})`
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminBackups;
